import { useTheme as useEmotionTheme } from '@emotion/react';
import { useMemo, useContext } from 'react';
import { ThemeProviderContext } from './ThemeProvider';
import {
  ColorResolveOptions,
  resolveColor,
  resolveColorProperties,
} from './colors';
import DEFAULT_THEME from './default';
import { ThemeConfig } from './theme';
import {
  resolveTypography,
  TypographyCSS,
  TypographyVariant,
} from './typography';
import { resolveSpacing, resolveSpacingProperties } from './spacing';
import { CSSProp, SxProp } from './sxProp';
import { compose } from '../utils/transform';

const resolveCSS =
  (theme: ThemeConfig, useThemeNext: boolean, isDarkMode: boolean) =>
  (sxValue?: SxProp): CSSProp =>
    compose(
      resolveSpacingProperties(theme),
      resolveColorProperties(theme, useThemeNext, isDarkMode)
    )(sxValue) as CSSProp;

type ThemeHook = {
  theme: ThemeConfig;
  useThemeNext: boolean;
  isDarkMode: boolean;
  resolveTypography: (variant: TypographyVariant) => TypographyCSS;
  resolveColor: (colorOptions: ColorResolveOptions) => string;
  resolveSpacing: (factor: number) => string;

  resolveCSS: (sxValue?: SxProp) => CSSProp;
};

const useTheme = (): ThemeHook => {
  const contextTheme = useEmotionTheme();
  const { useThemeNext, isDarkMode } = useContext(ThemeProviderContext);
  const theme = (
    Object.keys(contextTheme).length > 0 ? contextTheme : DEFAULT_THEME
  ) as ThemeConfig;

  const _resolveTypography = useMemo(() => resolveTypography(theme), [theme]);
  const _resolveColor = useMemo(
    () => resolveColor(theme, useThemeNext, isDarkMode),
    [theme, useThemeNext, isDarkMode]
  );
  const _resolveSpacing = useMemo(() => resolveSpacing(theme), [theme]);

  const _resolveCss = useMemo(
    () => resolveCSS(theme, useThemeNext, isDarkMode),
    [theme, useThemeNext, isDarkMode]
  );
  return {
    theme,
    useThemeNext,
    isDarkMode,
    resolveCSS: _resolveCss,
    resolveTypography: _resolveTypography,
    resolveColor: _resolveColor,
    resolveSpacing: _resolveSpacing,
  };
};

export default useTheme;
